import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiCheckCircle } from "react-icons/fi";
import { typography, spacing, components } from "../styles/designSystem";
import { getScrollVisibility, useScrollProgress } from "../styles/animations";
import { services } from "../data/servicesData";

const Services = () => {
  const sectionRef = useRef(null);
  const progress = useScrollProgress(sectionRef);
  const visibility = getScrollVisibility(progress);

  return (
    <section
      id="services-overview"
      ref={sectionRef}
      className="relative -mt-px overflow-hidden bg-white py-24 text-secondary md:py-36"
    >
      <style>
        {`
          @keyframes servicesGradientMove {
            0%, 100% {
              transform: translate3d(0, 0, 0) scale(1);
            }
            50% {
              transform: translate3d(30px, -25px, 0) scale(1.08);
            }
          }

          @keyframes servicesFloat {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-12px);
            }
          }

          @keyframes servicesShine {
            0% {
              transform: translateX(-160%) skewX(-18deg);
            }
            100% {
              transform: translateX(220%) skewX(-18deg);
            }
          }

          .services-orb {
            animation: servicesGradientMove 8s ease-in-out infinite;
          }

          .services-card:hover .services-shine {
            animation: servicesShine 0.9s ease forwards;
          }

          @media (prefers-reduced-motion: reduce) {
            .services-orb,
            .services-card:hover .services-shine {
              animation: none;
            }
          }
        `}
      </style>

      <div className={`relative z-10 ${spacing.container}`}>
        {/* Top section */}
        <div
          className="mx-auto max-w-4xl text-center"
          style={{
            opacity: visibility,
            transform: `translateY(${(1 - visibility) * 30}px)`,
            transition: "transform 0.05s linear, opacity 0.1s linear",
          }}
        >
          <p className={`${typography.label} text-gray-400`}>What we do</p>

          <h2 className={`${typography.heading.h2} mt-6 text-secondary`}>
            Digital support from idea to launch and growth
          </h2>

          <div
            className="mx-auto mt-6 h-1 w-14 rounded-full bg-gradient-to-r from-primary to-accent"
            style={{
              transform: `scaleX(${visibility})`,
              transformOrigin: "center",
            }}
          />

          <p className={`${typography.body.large} mx-auto mt-7 max-w-3xl`}>
            We help small businesses understand what they need, build the right
            digital solution, and improve their online presence.
          </p>
        </div>

        {/* Cards */}
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {services.map((service, index) => {
            return (
              <div
                key={service.title}
                className="h-full"
                style={{
                  opacity: visibility,
                  transform: `translateY(${(1 - visibility) * (45 + index * 15)}px)`,
                  transition: "transform 0.05s linear, opacity 0.12s linear",
                }}
              >
                <div className="services-card group relative h-full overflow-hidden rounded-[2rem] border border-secondary/10 bg-white/90 p-6 text-secondary shadow-lg backdrop-blur-md transition-all duration-500 hover:-translate-y-4 hover:scale-[1.02] hover:border-primary/30 hover:shadow-2xl md:p-7">
                  {/* Hover background */}
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/[0.09] via-transparent to-accent/[0.12] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                  {/* Shine */}
                  <div className="services-shine pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/80 to-transparent opacity-0 group-hover:opacity-100" />

                  {/* Background word */}
                  <span className="pointer-events-none absolute -right-5 -top-3 text-[4.5rem] font-black leading-none text-secondary/[0.035] transition-all duration-500 group-hover:scale-110 group-hover:text-primary/[0.07] md:text-[5.5rem]">
                    {service.backgroundWord}
                  </span>

                  {/* Top accent */}
                  <div className="absolute left-6 right-6 top-0 h-[3px] origin-left scale-x-0 rounded-full bg-gradient-to-r from-primary to-accent transition-transform duration-500 group-hover:scale-x-100" />

                  <div className="relative z-10 flex h-full flex-col">
                    <div className="flex items-start justify-between gap-5">
                      <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-primary/10 text-3xl text-primary transition-all duration-500 group-hover:-translate-y-1 group-hover:rotate-[-6deg] group-hover:scale-110 group-hover:bg-primary group-hover:text-white">
                        {service.icon}
                      </div>

                      <p
                        className={`${typography.card.base} max-w-[8rem] text-right text-secondary/35 group-hover:text-primary`}
                      >
                        {service.tag}
                      </p>
                    </div>

                    <h3
                      className={`${typography.card.large} text-3xl mt-8 group-hover:text-primary`}
                    >
                      {service.title}
                    </h3>

                    <p className={`${typography.body.base} mt-5`}>
                      {service.text}
                    </p>

                    <div className="mt-7 space-y-3">
                      {service.points.map((point) => (
                        <div
                          key={point}
                          className={`${typography.body.small} flex items-center gap-3`}
                        >
                          <FiCheckCircle className="shrink-0 text-primary" />
                          <span>{point}</span>
                        </div>
                      ))}
                    </div>

                    <Link
                      to="/services"
                      className="mt-auto flex items-center gap-2 pt-8 text-sm font-bold text-secondary/60 transition-colors duration-300 group-hover:text-primary"
                    >
                      View this service
                      <span className="flex items-center justify-center rounded-full bg-white/15 transition-transform duration-300 group-hover:translate-x-1">
                        <FiArrowRight />
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div
          className="mt-14 flex justify-center"
          style={{
            opacity: visibility,
            transform: `translateY(${(1 - visibility) * 20}px)`,
            transition: "transform 0.05s linear, opacity 0.1s linear",
          }}
        >
          <Link
            to="/services"
            className={`${components.button.primary} bg-secondary text-sm`}
          >
            Explore all services
            <FiArrowRight />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Services;
